/* eslint-disable */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Tag, X, Check, Clock, AlertCircle, EyeOff, Trash2 } from "lucide-react";
import API from "../lib/api";
import { errorMessage } from "../lib/errors";

/**
 * Phase 6.4 — Per-business "propose offers" UI on the My Business page.
 *
 * Lets a business owner submit an offer for review on a specific business.
 * Displays the list of their existing offers with status badges and a
 * Withdraw button.  No promo-code reveal (owner already knows their codes).
 */
export default function MyBusinessOffersInline({ business }) {
  const { t } = useTranslation();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    discount_label: "",
    promo_code: "",
    ends_at: "",
    terms: "",
  });

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/business/offers/mine/list");
      setOffers((data.items || []).filter((o) => o.business_id === business.business_id));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [business?.business_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.discount_label || !form.promo_code) {
      return toast.error(t("offers.owner_missing_fields", "Please fill the required fields"));
    }
    setSubmitting(true);
    try {
      await API.post("/business/offers", {
        business_id: business.business_id,
        title: form.title.trim(),
        description: form.description.trim(),
        discount_label: form.discount_label.trim(),
        promo_code: form.promo_code.trim().toUpperCase(),
        ends_at: form.ends_at ? new Date(form.ends_at).toISOString() : null,
        terms: form.terms.trim() || null,
      });
      toast.success(t("offers.owner_submitted", "Offer submitted for review"));
      setForm({ title: "", description: "", discount_label: "", promo_code: "", ends_at: "", terms: "" });
      setShowForm(false);
      reload();
    } catch (e) {
      toast.error(errorMessage(e, t("offers.owner_submit_failed", "Couldn't submit the offer")));
    } finally {
      setSubmitting(false);
    }
  };

  const withdraw = async (offer_id) => {
    if (!window.confirm(t("offers.owner_confirm_delete", "Withdraw this offer?"))) return;
    try {
      await API.delete(`/business/offers/${offer_id}`);
      toast.success(t("offers.owner_withdrawn", "Offer withdrawn"));
      reload();
    } catch (e) { toast.error(errorMessage(e)); }
  };

  return (
    <div className="mt-4 border-t border-white/10 pt-4" data-testid={`my-biz-offers-${business.business_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow !mb-0 inline-flex items-center gap-2">
          <Tag className="w-3 h-3 gold-text" /> {t("offers.owner_member_benefits", "Member Benefits")}
        </div>
        <button
          onClick={() => setShowForm((s) => !s)}
          className="text-xs gold-text hover:underline inline-flex items-center gap-1"
          data-testid={`my-biz-offer-toggle-${business.business_id}`}
        >
          {showForm ? <><X className="w-3 h-3" /> {t("offers.owner_close", "Close")}</> : <><Plus className="w-3 h-3" /> {t("offers.owner_propose", "Propose offer")}</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="grid gap-2 mb-4 p-3 rounded-xl border border-white/10 bg-white/[0.02]" data-testid={`my-biz-offer-form-${business.business_id}`}>
          <input
            placeholder={t("offers.owner_title_ph", "Offer title")}
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            required
            className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
          />
          <textarea
            placeholder={t("offers.owner_desc_ph", "Short description")}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={2}
            className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
          />
          <div className="grid sm:grid-cols-3 gap-2">
            <input
              placeholder={t("offers.owner_discount_ph", "Discount (e.g. 20% OFF)")}
              value={form.discount_label}
              onChange={(e) => setForm({ ...form, discount_label: e.target.value })}
              required
              className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
            <input
              placeholder={t("offers.owner_code_ph", "Promo code")}
              value={form.promo_code}
              onChange={(e) => setForm({ ...form, promo_code: e.target.value.toUpperCase() })}
              required
              className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm font-mono"
            />
            <input
              type="date"
              value={form.ends_at}
              onChange={(e) => setForm({ ...form, ends_at: e.target.value })}
              className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <textarea
            placeholder={t("offers.owner_terms_ph", "Terms (optional)")}
            value={form.terms}
            onChange={(e) => setForm({ ...form, terms: e.target.value })}
            rows={2}
            className="bg-[#0F1116] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none"
          />
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary text-xs disabled:opacity-60 mt-1"
            data-testid={`my-biz-offer-submit-${business.business_id}`}
          >
            {submitting ? t("offers.owner_submitting", "Submitting…") : t("offers.owner_submit", "Submit for review")}
          </button>
          <p className="text-[11px] text-white/45">{t("offers.owner_review_note", "Your offer will be reviewed by an admin before going live.")}</p>
        </form>
      )}

      {loading ? (
        <div className="text-xs text-white/55">{t("common.loading", "Loading…")}</div>
      ) : offers.length === 0 ? (
        <div className="text-xs text-white/55">{t("offers.owner_none_yet", "No offers proposed yet.")}</div>
      ) : (
        <ul className="space-y-1.5">
          {offers.map((o) => (
            <li key={o.offer_id} className="rounded-lg border border-white/10 px-3 py-2 flex items-center justify-between gap-3" data-testid={`my-biz-offer-row-${o.offer_id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-xs text-white/55 flex items-center gap-1.5">
                  <StatusBadge status={o.status} />
                  <span className="font-mono text-[11px] text-white/65">{o.promo_code}</span>
                </div>
                <div className="text-sm text-white/90 truncate">{o.discount_label} — {o.title}</div>
                {o.rejection_reason && (
                  <div className="text-[11px] text-rose-300 mt-0.5">Reason: {o.rejection_reason}</div>
                )}
              </div>
              <button
                onClick={() => withdraw(o.offer_id)}
                className="text-[11px] text-white/55 hover:text-rose-300 inline-flex items-center gap-1"
                data-testid={`my-biz-offer-delete-${o.offer_id}`}
              >
                <Trash2 className="w-3 h-3" /> {t("offers.owner_withdraw", "Withdraw")}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const colors = {
    pending: { Icon: Clock, cls: "text-amber-300" },
    approved: { Icon: Check, cls: "text-emerald-300" },
    rejected: { Icon: AlertCircle, cls: "text-rose-300" },
    hidden: { Icon: EyeOff, cls: "text-white/55" },
    expired: { Icon: Clock, cls: "text-white/45" },
  }[status] || { Icon: Clock, cls: "text-white/55" };
  const Icon = colors.Icon;
  return (
    <span className={`inline-flex items-center gap-1 ${colors.cls} text-[10px] uppercase tracking-[0.22em]`}>
      <Icon className="w-3 h-3" /> {status}
    </span>
  );
}
